import type { TFunction } from 'i18next'
import {
  isoDateToLocalDate,
  localDateToIsoDate,
} from '@/lib/date'
import { monthsBetween } from '@/lib/format'
import type {
  ContractTimeSlot,
  ContractType,
  PlanningFrequency,
} from '@/types'
import type { Milestone } from './types'

export const VAT_RATES = [0, 5.5, 10, 20]

export const QUICK_DURATIONS = [
  { id: '6m', months: 6 },
  { id: '1y', months: 12 },
  { id: '2y', months: 24 },
] as const

export const BILLING_FREQUENCIES: { value: string; invoices: number }[] = [
  { value: 'monthly', invoices: 12 },
  { value: 'quarterly', invoices: 4 },
  { value: 'biannual', invoices: 2 },
  { value: 'yearly', invoices: 1 },
]

export const TIME_SLOTS: ContractTimeSlot[] = [
  { start: '08:00', end: '10:00' },
  { start: '10:00', end: '12:00' },
  { start: '13:00', end: '15:00' },
  { start: '15:00', end: '17:00' },
]

export const RENEWAL_DEADLINE_CARDS: { value: Milestone; titleKey: string }[] = [
  { value: '6m', titleKey: '6m' },
  { value: '1y', titleKey: '1y' },
  { value: '2y', titleKey: '2y' },
]

export const CONTRACT_TYPE_DOT: Record<ContractType, string> = {
  rodent_control: '#8a5405',
  insect_control: '#ff5a1f',
  haccp: '#065f46',
  multi_site: '#1d44b3',
  rodent_insect_control: '#ca8a04',
}

export const PLANNING_CARD_FREQUENCIES: {
  value: PlanningFrequency
  visits: number
}[] = [
  { value: 'monthly', visits: 12 },
  { value: 'bimonthly', visits: 6 },
  { value: 'quarterly', visits: 4 },
  { value: 'biannual', visits: 2 },
  { value: 'annually', visits: 1 },
]

export function addMonthsMinusDay(isoStart: string, months: number): string {
  const d = isoDateToLocalDate(isoStart)
  if (!d) return ''
  d.setMonth(d.getMonth() + months)
  d.setDate(d.getDate() - 1)
  return localDateToIsoDate(d)
}

export function addMilestone(base: Date, milestone: Milestone): Date {
  const d = new Date(base)
  if (milestone === '30d') d.setDate(d.getDate() + 30)
  if (milestone === '3m') d.setMonth(d.getMonth() + 3)
  if (milestone === '6m') d.setMonth(d.getMonth() + 6)
  if (milestone === '1y') d.setFullYear(d.getFullYear() + 1)
  if (milestone === '2y') d.setFullYear(d.getFullYear() + 2)
  d.setDate(d.getDate() - 1)
  return d
}

export function durationLabel(start: string, end: string, t: TFunction): string {
  if (!start || !end) return '-'
  const s = new Date(start)
  const e = new Date(end)
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '-'
  const months = monthsBetween(s, e)
  if (months < 0) return '-'
  if (months >= 12 && months % 12 === 0) {
    const y = months / 12
    return t('form.durationLabel.year', { count: y })
  }
  return t('form.durationLabel.month', { count: months })
}
